'use client'

import React from 'react'
import { useTranslations } from 'next-intl'
import { Box, Typography, IconButton, Link as MuiLink, Container } from '@mui/material'
import { SvgIconComponent } from '@mui/icons-material'
import Image from 'next/image'
import FlyinLogo from '@/src/assets/png/FlyinLogo.png'

interface FooterLink {
    label: string
    onClick: () => void
}

interface SocialMediaLink {
    name: string
    href: string
    icon: SvgIconComponent | string
}

interface HomepageFooterProps {
    companyLinks: FooterLink[]
    supportLinks: FooterLink[]
    socialMediaLinks: SocialMediaLink[]
    isSignedIn?: boolean
}

const HomepageFooter: React.FC<HomepageFooterProps> = ({
    companyLinks,
    supportLinks,
    socialMediaLinks,
    isSignedIn = false
}) => {
    const t = useTranslations('footer')
    return (
        <Box sx={{ bgcolor: 'grey.100', pt: 4, pb: 3 }}>
            <Container maxWidth="xl" sx={{ px: { xs: 2, md: 3 } }}>
                {/* Top Section: Logo + License Info + Social Icons */}
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        pb: 3
                    }}
                >
                    {/* Left: Logo and License Info */}
                    <Box>
                        {/* Logo */}
                        <Box sx={{ mb: 2 }}>
                            <Image src={FlyinLogo} alt="Flyin.com" width={100} height={42} />
                        </Box>

                        {/* License Information */}
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            <Typography
                                variant="body2"
                                sx={{
                                    color: 'text.primary',
                                    fontSize: '13px',
                                    lineHeight: 1.6
                                }}
                            >
                                {t('travelLicense')}:{' '}
                                <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>
                                    73100559
                                </Box>
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{
                                    color: 'text.primary',
                                    fontSize: '13px',
                                    lineHeight: 1.6
                                }}
                            >
                                {t('licenseCategory')}:{' '}
                                <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>
                                    {t('travelAndTourismAgency')}
                                </Box>
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{
                                    color: 'text.primary',
                                    fontSize: '13px',
                                    lineHeight: 1.6
                                }}
                            >
                                {t('registrationNumber')}:{' '}
                                <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>
                                    7001623003
                                </Box>
                            </Typography>
                        </Box>
                    </Box>

                    {/* Right: Social Media Icons */}
                    <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                        {socialMediaLinks.map(social => (
                            <IconButton
                                key={social.name}
                                href={social.href}
                                target="_blank"
                                sx={{
                                    bgcolor: 'common.white',
                                    width: 48,
                                    height: 48,
                                    borderRadius: '12%',
                                    '&:hover': { bgcolor: 'grey.200', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }
                                }}
                            >
                                {social.icon === 'twitter' ? (
                                    <Box
                                        component="svg"
                                        viewBox="0 0 24 24"
                                        sx={{ width: 24, height: 24, fill: 'currentColor', color: 'text.primary' }}
                                    >
                                        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                                    </Box>
                                ) : (
                                    React.createElement(social.icon as SvgIconComponent, {
                                        sx: { color: 'text.primary', fontSize: 24 }
                                    })
                                )}
                            </IconButton>
                        ))}
                    </Box>
                </Box>

                {/* Divider */}
                <Box sx={{ height: '1px', bgcolor: 'divider', my: 3 }} />

                {/* Bottom Section: Links + VAT + Copyright */}
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}
                >
                    {/* Left: Navigation Links */}
                    <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                        {companyLinks.map((link, index) => (
                            <MuiLink
                                key={index}
                                component="button"
                                onClick={link.onClick}
                                sx={{
                                    color: 'text.secondary',
                                    textDecoration: 'none',
                                    fontSize: '14px',
                                    textTransform: 'none',
                                    '&:hover': { color: 'primary.main' }
                                }}
                            >
                                {link.label}
                            </MuiLink>
                        ))}
                        {supportLinks
                            .filter(link => link.label === t('fileClaim'))
                            .map((link, index) => (
                                <MuiLink
                                    key={`file-claim-${index}`}
                                    component="button"
                                    onClick={link.onClick}
                                    sx={{
                                        color: 'text.secondary',
                                        textDecoration: 'none',
                                        fontSize: '14px',
                                        textTransform: 'none',
                                        '&:hover': { color: 'primary.main' }
                                    }}
                                >
                                    {link.label}
                                </MuiLink>
                            ))}
                    </Box>

                    {/* Right: VAT Logo + Copyright */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <MuiLink href="https://ui.cltp.co/flyin-static/docs/flyin-VAT-cert.pdf" target="_blank">
                            <Box
                                component="img"
                                src="https://ui.cltp.co/flyin-static/img/vat-cert-logo.png"
                                alt="VAT"
                                sx={{ width: 40, height: 40 }}
                            />
                        </MuiLink>
                        <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '13px', fontWeight: 400 }}>
                            {t('copyright', { year: new Date().getFullYear() })}
                        </Typography>
                    </Box>
                </Box>
            </Container>
        </Box>
    )
}

export { HomepageFooter }
export type { HomepageFooterProps }
